import { engine } from '@play-co/timestep-core';
import { ImageView, View } from '@play-co/timestep-core/ui';
import { captureGenericError } from 'src/lib/sentry';
import {
  getApplication,
  getScreenDimensions,
  getScreenExtraVerticalSpace,
} from 'src/lib/utils';
import { getHandTarget } from './TutorialHandService';
import { WebGLContext } from '@play-co/timestep-core/lib/platforms/browser/webgl/WebGLContext2D';
import { GLManager } from '@play-co/timestep-core/renderer';
import { TutorialArrow } from 'src/game/components/shared/TutorialArrow';
import { TutorialPointer } from 'src/game/components/shared/TutorialPointer';
import NakedPromise from 'src/game/minigame/match2-timestep/lib/pattern/NakedPromise';
import gsap from 'gsap';

export type TutorialHighlightItemArgs = {
  tag: string;
  arrow?: {
    x?: number;
    y?: number;
    r?: number;
  };
  pointer?: {
    x?: number;
    y?: number;
    r?: number;
    slideX?: number;
    slideY?: number;
  };
};

type TutorialHighlightItem = {
  originalView: View;
  renderedView: ImageView;
  arrow?: TutorialArrow;
  pointer?: TutorialPointer;
  ctx?: WebGLContext;
  showing?: boolean;
};

/**
 * Service that highlight specific views with optional pointers and arrows.
 * It renders the target view to an overlay image and displays it with optional animations.
 * The rendered view will take control of the original view input events.
 * KNOWN ISSUE: Sometimes it doesn't work well with ImageScaleView as targets, because of scaling and offsets.
 * A workaround for those cases is to insert the image into another view, and use that instead.
 */
export class TutorialHighlightService {
  private container: View;
  private bg: View;
  private items: Record<string, TutorialHighlightItem> = {};
  private showing = false;

  constructor() {
    this.container = new View({
      clip: true,
      visible: false,
      canHandleEvents: false,
      zIndex: 10000, // Ensure it is on top of everything
    });

    // Create overlay fill
    this.bg = new View({
      superview: this.container,
      backgroundColor: '#000000CC',
      opacity: 0,
    });

    this.container.addListener('ViewAdded', () => {
      engine.unsubscribe('Tick', this.tick); // Ensure we don't add multiple listeners
      engine.subscribe('Tick', this.tick);
    });

    this.container.addListener('ViewRemoved', () => {
      engine.unsubscribe('Tick', this.tick);
    });
  }

  public addTarget(args: TutorialHighlightItemArgs) {
    if (this.items[args.tag]) return;

    const originalView = getHandTarget(args.tag);
    if (!originalView) {
      captureGenericError(
        `TutorialArrowServiceError`,
        `No view found for tag ${args.tag}`,
      );
      return;
    }

    const renderedView = new ImageView({
      superview: this.container,
      opacity: 0,
    });

    const inputTarget = getInputTarget(originalView);

    // The rendered view will take control of the original view input events.
    renderedView.onInputStart = (...args) => {
      inputTarget?.onInputStart?.bind(inputTarget)(...args);
    };
    renderedView.onInputSelect = (...args) => {
      inputTarget?.onInputSelect?.bind(inputTarget)(...args);
    };
    renderedView.onInputOut = (...args) => {
      inputTarget?.onInputOut?.bind(inputTarget)(...args);
    };
    renderedView.onInputOver = (...args) => {
      inputTarget?.onInputOver?.bind(inputTarget)(...args);
    };
    renderedView.onInputMove = (...args) => {
      inputTarget?.onInputMove?.bind(inputTarget)(...args);
    };

    const item: TutorialHighlightItem = {
      originalView,
      renderedView,
    };

    if (args.arrow) {
      item.arrow = new TutorialArrow({
        superview: this.container,
        x: args.arrow.x ?? 0,
        y: args.arrow.y ?? 0,
        r: args.arrow.r ?? 0,
        zIndex: 1,
        opacity: 0,
      });
    }

    if (args.pointer) {
      item.pointer = new TutorialPointer({
        superview: this.container,
        x: args.pointer.x ?? 0,
        y: args.pointer.y ?? 0,
        r: args.pointer.r ?? 0,
        slideX: args.pointer.slideX ?? 0,
        slideY: args.pointer.slideY ?? 0,
        zIndex: 1,
        opacity: 0,
      });
    }

    this.items[args.tag] = item;
    if (this.showing) this.showItem(args.tag);
  }

  public clearTargets() {
    for (const tag in this.items) {
      const target = this.items[tag];
      target.renderedView.removeFromSuperview();
      target.arrow?.removeFromSuperview();
      target.pointer?.removeFromSuperview();
      target.ctx?.clear();
    }
    this.items = {};
  }

  public async show() {
    if (this.showing) return;
    this.showing = true;

    this.container.updateOpts({
      superview: getApplication().getRootView(),
    });

    this.container.updateOpts({
      visible: true,
    });

    Object.keys(this.items).forEach((tag) => {
      this.showItem(tag);
    });

    gsap.killTweensOf(this.bg.style);
    await gsap.to(this.bg.style, {
      opacity: 1,
      duration: 0.5,
      ease: 'linear',
    });

    this.enableInteraction();
  }

  public async hide() {
    if (!this.showing) return;
    this.showing = false;

    this.disableInteraction();
    Object.keys(this.items).forEach((tag) => {
      this.hideItem(tag);
    });
    gsap.killTweensOf(this.bg.style);
    await gsap.to(this.bg.style, {
      opacity: 0,
      duration: 0.2,
      ease: 'linear',
    });
    this.clearTargets();
    this.container.hide();
    this.container.removeFromSuperview();
  }

  public enableInteraction() {
    this.container.updateOpts({
      canHandleEvents: true,
    });
  }

  public disableInteraction() {
    this.container.updateOpts({
      canHandleEvents: false,
    });
  }

  private getScrenOffsetY() {
    return getScreenExtraVerticalSpace() / 2;
  }

  private tick = () => {
    if (!this.container.style.visible) return;

    const root = getApplication().getRootView();
    const width = root.style.width;
    const height = getScreenDimensions().height;

    this.container.updateOpts({
      width: width,
      height: height,
      y: -this.getScrenOffsetY(),
      visible: true,
    });

    this.bg.updateOpts({
      width: width,
      height: height,
    });

    // Just flush the context manager before rendering the items.
    (GLManager as any).flush();

    for (const tag in this.items) {
      this.renderItem(tag);
    }
  };

  private renderItem(tag: string) {
    // Skip if there is no target or if the overlay is not visible.
    if (!this.bg.style.opacity) return;

    const target = this.items[tag];
    if (!target) return;

    const { originalView, renderedView } = target;
    const root = getApplication().getRootView();

    // Skip if target is not in root.
    if (!originalView.getSuperviews().includes(root)) return;

    const renderWidth = originalView.style.width;
    const renderHeight = originalView.style.height;

    // If there is a cached context, clear it before rendering again
    target.ctx?.clear();

    // Render the target to the overlay image
    const image = originalView.style.renderToImage(tag, {
      width: renderWidth,
      height: renderHeight,
      offsetX: 0,
      offsetY: 0,
    });

    // Store the context used in the image to clear it before next render
    if (image.src instanceof WebGLContext) {
      target.ctx = image.src as WebGLContext;
    }

    // Update with the rendered image
    renderedView.updateOpts({ image });

    // Update the position of the rendered image to match the target position.
    const position = this.getTargetPosition(tag);
    position.y += this.getScrenOffsetY();
    renderedView.updateOpts({
      x: position.x,
      y: position.y,
      width: position.width,
      height: position.height,
      scale: position.scale,
      anchorX: position.anchorX,
      anchorY: position.anchorY,
    });

    if (target.arrow) {
      target.arrow.updateOpts({
        offsetX: position.x + position.width * 0.5,
        offsetY: position.y + position.height * 0.5,
      });
    }

    if (target.pointer) {
      target.pointer.updateOpts({
        offsetX: position.x + position.width * 0.5,
        offsetY: position.y + position.height * 0.5,
      });
    }
  }

  public getTargetPosition(tag: string) {
    if (!this.items[tag]) {
      return {
        x: 0,
        y: 0,
        r: 0,
        width: 0,
        height: 0,
        scale: 0,
        anchorX: 0,
        anchorY: 0,
      };
    }
    const root = getApplication().getRootView();
    const position = this.items[tag].originalView.getPosition(root);
    return position;
  }

  private async showItem(tag: string) {
    const target = this.items[tag];
    if (!target || target.showing) return;
    target.showing = true;

    if (target.arrow) {
      target.arrow.fadeIn();
    }

    if (target.pointer) {
      target.pointer.fadeIn();
    }

    gsap.killTweensOf(target.renderedView.style);
    await gsap.to(target.renderedView.style, {
      opacity: 1,
      duration: 0.3,
      ease: 'linear',
    });
  }

  private async hideItem(tag: string) {
    const target = this.items[tag];
    if (!target || !target.showing) return;
    target.showing = false;

    if (target.arrow) {
      target.arrow.fadeOut();
    }

    gsap.killTweensOf(target.renderedView.style);
    await gsap.to(target.renderedView.style, {
      opacity: 0,
      duration: 0.2,
      ease: 'linear',
    });
  }
}

/**
 * Find the input target - can be the target view itself or one of its superviews.
 */
function getInputTarget(view: View) {
  if (view.onInputStart) {
    return view;
  }

  const parent = view.getSuperview();
  if (parent) {
    return getInputTarget(parent);
  }

  return undefined;
}
